import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { BadgeModule } from 'primeng/badge';
import { RippleModule } from 'primeng/ripple';
import { finalize, map, tap } from 'rxjs';
import { IconKey } from '../../../../../../whitelabel/icon-key.enum';
import { BadgeComponent } from '../../../../../osp-ui/components/misc/badge/badge.component';
import { SpinnerComponent } from '../../../../../osp-ui/components/misc/spinner/spinner.component';
import { OverlayPanelComponent } from '../../../../../osp-ui/components/overlay/overlay-panel/overlay-panel.component';
import { ComponentSize } from '../../../../../osp-ui/enums/component-size.enum';
import { AttentionStatusTagComponent } from '../../../../../pages/resources/equipment/components/attention-status-tag/attention-status-tag.component';
import { EquipmentAttention } from '../../../../../pages/resources/equipment/contract/requests/equipment-attention.contract';
import { EquipmentsApiService } from '../../../../../pages/resources/equipment/services/equipments-api.service';
import { DynamicThumbnailComponent } from '../../../../../shared/components/dynamic-thumbnail/dynamic-thumbnail.component';
import { Authorities } from '../../../../../shared/enums/authorities.enum';
import { Modules } from '../../../../../shared/enums/modules.enum';
import { AddressPipe } from '../../../../../shared/pipes/address.pipe';
import { IconPipe } from '../../../../../shared/pipes/icon.pipe';
import { ToastService } from '../../../../../shared/services/toast.service';
import { AuthService } from '../../../../services/auth.service';
import { LanguageService } from '../../../../services/language.service';

@Component({
  selector: 'osp-equipment-attention-overlay',
  standalone: true,
  imports: [
    RippleModule,
    OverlayPanelComponent,
    CommonModule,
    BadgeModule,
    BadgeComponent,
    TranslateModule,
    AddressPipe,
    DynamicThumbnailComponent,
    AttentionStatusTagComponent,
    IconPipe,
    SpinnerComponent,
  ],
  templateUrl: './equipment-attention-overlay.component.html',
  styleUrls: ['./equipment-attention-overlay.component.scss', '../topbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EquipmentAttentionOverlayComponent implements OnInit {
  @ViewChild('op') overlayPanel!: OverlayPanelComponent;
  private equipmentApiService = inject(EquipmentsApiService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);
  private toastService = inject(ToastService);
  private authService = inject(AuthService);
  private languageService = inject(LanguageService);

  protected canSeeEvents = this.authService.hasModule(Modules.EQUIPMENT_EVENT_MANAGEMENT)
    && this.authService.hasAuthority(Authorities.EQUIPMENT_EVENT_VIEW);

  protected readonly ComponentSize = ComponentSize;
  protected readonly IconKey = IconKey;

  $attentionEquipments = signal<EquipmentAttention[]>([]);
  $attentionEquipmentsTotalAmount = signal(0);
  $isListLoading = signal(false);
  $isNoData = computed(() => this.$attentionEquipments().length && !this.$isListLoading());

  ngOnInit(): void {
    this.getAttentionEquipments();
  }

  private getAttentionEquipments(): void {
    this.$isListLoading.set(true);

    this.equipmentApiService
      .getAttentionEquipments()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(({ totalElements }) => this.$attentionEquipmentsTotalAmount.set(totalElements)),
        map(({ content }) => content),
        /*switchMap((equipments) =>
          forkJoin(equipments.map((equipment) => this.getAttentionEquipmentExtended(equipment))),
        ),*/
        finalize(() => this.$isListLoading.set(false)),
      )
      .subscribe((equipments) => this.$attentionEquipments.set(equipments));
  }

  public onItemClick(equipment: EquipmentAttention): void {
    if (!this.canSeeEvents || !equipment.equipmentId) {
      this.toastService.showNeutral({
        summary: this.languageService.getInstant('revolution.modules.attentionStatus.navigationToEventIsNotPossible')
      });
      return;
    }

    this.overlayPanel.hide();
    this.router.navigate([`/resources/equipment/${equipment.equipmentId}/events/active-logs`]);
  }

}
